import { _lcs } from "../../i18n";

/**
 * Language switch for records that carry machine translations
 * (`translations: { en: { title, teaser, … }, … }`, filled by the core-api
 * import — see ContentTranslationService).
 *
 * Not a web component: the dialogs that use it already own their markup and
 * lifecycle, so this renders into a container they provide and reports the
 * chosen language back. Styles: `.lang-switch` in @belturo/design-system.
 *
 * The same module exists in client-admin (apps do not share JS).
 */

/** Languages that actually hold at least one translated field, en first. */
export function translationLanguages( record )
{
    const translations = record && record.translations && typeof record.translations === "object"
        ? record.translations
        : {};

    return Object.keys( translations )
        .filter( ( lang ) => translations[ lang ] && Object.values( translations[ lang ] ).some( hasText ) )
        .sort( ( a, b ) => ( a === "en" ? -1 : b === "en" ? 1 : a.localeCompare( b ) ) );
}

/**
 * A field in the chosen language. The source language reads the record
 * itself; a translation without that field yields "" — the source left it
 * empty, so there is nothing to show either.
 */
export function translatedField( record, lang, field )
{
    const source = ( record && record.language ) || "de";
    if ( !lang || lang === source )
    {
        return record ? ( record[ field ] || "" ) : "";
    }
    const fields = record.translations && record.translations[ lang ];
    return fields && typeof fields[ field ] === "string" ? fields[ field ] : "";
}

/**
 * Render the switch into `container` and call `onChange( lang )` on a click.
 *
 * @param {HTMLElement} container
 * @param {Object}   record
 * @param {Object}   [options]
 * @param {string}   [options.active]           Initially selected language.
 * @param {boolean}  [options.includeOriginal=true] Offer the source language too.
 * @param {Function} [options.onChange]
 * @returns {string[]} The translation languages found (empty = nothing rendered but a hint).
 */
export function renderTranslationSwitch( container, record, { active = null, includeOriginal = true, onChange = null } = {} )
{
    const source = ( record && record.language ) || "de";
    const languages = translationLanguages( record );

    if ( languages.length === 0 )
    {
        container.innerHTML =
            '<p class="lang-switch__note"><i class="icon icon--languages"></i> ' +
                esc( _lcs( "Noch keine Übersetzungen vorhanden." ) ) +
            '</p>';
        return languages;
    }

    const options = includeOriginal ? [ source, ...languages ] : languages;
    let current = options.includes( active ) ? active : options[ 0 ];

    const paint = () =>
    {
        container.innerHTML =
            '<div class="lang-switch">' +
                '<div class="lang-switch__group" role="group" aria-label="' + esc( _lcs( "Sprache" ) ) + '">' +
                    options.map( ( lang ) =>
                        '<button type="button" class="lang-switch__btn" data-lang="' + esc( lang ) + '"' +
                            ' aria-pressed="' + ( lang === current ? "true" : "false" ) + '"' +
                            ' title="' + esc( languageName( lang ) ) + '">' +
                            esc( lang.toUpperCase() ) +
                            ( lang === source ? ' <span class="lang-switch__tag">' + esc( _lcs( "Original" ) ) + '</span>' : "" ) +
                        '</button>'
                    ).join( "" ) +
                '</div>' +
                ( current !== source
                    ? '<p class="lang-switch__note"><i class="icon icon--sparkles"></i> ' +
                        esc( _lcs( "Maschinell übersetzt" ) ) + '</p>'
                    : "" ) +
            '</div>';
    };

    paint();

    // One listener on the container: paint() replaces the buttons, and a
    // dialog reopened for another record must not stack handlers.
    container.onclick = ( e ) =>
    {
        const btn = e.target.closest( "[data-lang]" );
        if ( !btn ) return;
        const lang = btn.getAttribute( "data-lang" );
        if ( lang === current ) return;
        current = lang;
        paint();
        if ( onChange ) onChange( lang );
    };

    return languages;
}

/** A non-empty string, or a list holding one (a dialog's normalised "teasers"). */
function hasText( value )
{
    if ( Array.isArray( value ) ) return value.some( hasText );
    return typeof value === "string" && value.trim() !== "";
}

/** "ru" → "русский" — in its own language, which is how people look for it. */
function languageName( lang )
{
    try
    {
        const name = new Intl.DisplayNames( [ lang ], { type: "language" } ).of( lang ) || lang;
        return name.charAt( 0 ).toLocaleUpperCase( lang ) + name.slice( 1 );
    }
    catch ( e )
    {
        return lang;
    }
}

function esc( value )
{
    return String( value === null || value === undefined ? "" : value )
        .replace( /&/g, "&amp;" )
        .replace( /</g, "&lt;" )
        .replace( />/g, "&gt;" )
        .replace( /"/g, "&quot;" );
}
